import { useEffect, useMemo, useRef, useState } from "react";
import { AppIcon, MetricCard } from "../components/InstateamMasterComponents";
import {
  getAdminAttendanceOverview,
  importAdminAttendanceRecords,
  reviewAdminCompOffRequest,
  reviewAdminAttendanceRegularization,
  updateAdminAttendanceRecord,
} from "../api/instateamAttendanceAdminApi";
import {
  buildAdminAttendanceCalendar,
  createAttendanceDonutStyle,
  downloadAttendanceCsv,
  formatAttendanceStatus,
  formatMonthHeading,
  getAttendanceStatusMeta,
  getCurrentDateKey,
  getCurrentMonthKey,
  parseAttendanceCsv,
  shiftMonthKey,
  toTimeInputValue,
} from "../utils/adminAttendanceHelpers";
import { getInitials, toAbsoluteUrl } from "../utils/adminHelpers";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

function getCsvDate(record, fallbackDateKey = "") {
  if (record?.dateKey) return `'${record.dateKey}`;
  if (record?.date) {
    const date = new Date(record.date);
    if (!Number.isNaN(date.getTime())) return `'${date.toISOString().slice(0, 10)}`;
  }
  return fallbackDateKey ? `'${fallbackDateKey}` : "";
}

const INITIAL_RECORD_FORM = {
  status: "present",
  checkInTime: "",
  checkOutTime: "",
  location: "Office",
};

function AttendanceRecordModal({
  row,
  form,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
  selectedDateKey,
}) {
  if (!row) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal attendance-record-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>Update Attendance</h3>
            <p>
              Adjust the attendance entry for {row.fullName} on {selectedDateKey}.
            </p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <form className="department-modal-form" onSubmit={onSubmit}>
          <div className="attendance-form-grid">
            <label className="form-field">
              <span>Status</span>
              <div className="field-shell select-shell">
                <select
                  value={form.status}
                  onChange={(event) =>
                    onChange((current) => ({ ...current, status: event.target.value }))
                  }
                >
                  <option value="present">Present</option>
                  <option value="late">Late</option>
                  <option value="absent">Absent</option>
                  <option value="on_leave">On Leave</option>
                  <option value="weekly_off">Weekly Off</option>
                  <option value="holiday">Holiday</option>
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>Check In Time</span>
              <div className="field-shell">
                <input
                  type="time"
                  value={form.checkInTime}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      checkInTime: event.target.value,
                    }))
                  }
                />
              </div>
            </label>

            <label className="form-field">
              <span>Check Out Time</span>
              <div className="field-shell">
                <input
                  type="time"
                  value={form.checkOutTime}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      checkOutTime: event.target.value,
                    }))
                  }
                />
              </div>
            </label>

            <label className="form-field">
              <span>Location</span>
              <div className="field-shell">
                <input
                  type="text"
                  value={form.location}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      location: event.target.value,
                    }))
                  }
                  placeholder="Office"
                />
              </div>
            </label>
          </div>

          <div className="form-footer">
            <span className="required-note">
              Use this when admin needs to correct or mark attendance manually.
            </span>
            <div className="form-footer-actions">
              <button className="secondary-button" type="button" onClick={onClose}>
                Cancel
              </button>
              <button className="primary-button save-button" type="submit">
                {isSubmitting ? "Saving..." : "Save Attendance"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function CompOffRequestsModal({ requests, onClose, onReview, isSubmitting }) {
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal attendance-comp-off-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>Comp Off Requests</h3>
            <p>Review holiday-work requests and approve earned comp off credit.</p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="attendance-comp-off-list">
          {requests.length ? (
            requests.map((record) => (
              <article key={record._id} className="attendance-comp-off-item">
                <div className="attendance-request-person">
                  <div className="avatar-circle small">
                    {getInitials(record.fullName)}
                  </div>
                  <div>
                    <strong>{record.fullName}</strong>
                    <span>{record.designation}</span>
                  </div>
                </div>

                <div className="attendance-comp-off-copy">
                  <strong>{record.dateLabel}</strong>
                  <p>{record.compOffRequest?.reason || "No reason was shared."}</p>
                  <small>
                    Worked {record.checkInTime || "-"} to {record.checkOutTime || "-"}
                  </small>
                </div>

                <div className="attendance-request-actions">
                  <button
                    type="button"
                    className="status-badge active action-button"
                    onClick={() => onReview(record._id, "approved")}
                    disabled={isSubmitting}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="status-badge inactive action-button"
                    onClick={() => onReview(record._id, "rejected")}
                    disabled={isSubmitting}
                  >
                    Reject
                  </button>
                </div>
              </article>
            ))
          ) : (
            <div className="directory-empty-state">
              No pending comp off requests right now.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AttendancePage({
  token,
  departmentOptions,
  searchText: headerSearchText,
  onSearchChange,
  onProtectedError,
  onNotify,
}) {
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey);
  const [selectedDateKey, setSelectedDateKey] = useState(getCurrentDateKey);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [localSearchText, setLocalSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [recordForm, setRecordForm] = useState(INITIAL_RECORD_FORM);
  const [showCompOffModal, setShowCompOffModal] = useState(false);
  const fileInputRef = useRef(null);
  const pendingRequestsRef = useRef(null);
  const searchText = headerSearchText ?? localSearchText;
  const setSearchText = onSearchChange || setLocalSearchText;

  useEffect(() => {
    if (!selectedDateKey.startsWith(monthKey)) {
      setSelectedDateKey(
        monthKey === getCurrentMonthKey() ? getCurrentDateKey() : `${monthKey}-01`
      );
    }
  }, [monthKey, selectedDateKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, departmentFilter, locationFilter, pageSize]);

  useEffect(() => {
    let isActive = true;

    async function fetchOverview() {
      try {
        setIsLoading(true);
        const payload = await getAdminAttendanceOverview(token, {
          month: monthKey,
          date: selectedDateKey,
          department: departmentFilter,
          location: locationFilter,
          search: searchText,
        });

        if (!isActive) return;

        setOverview(payload.overview || null);
      } catch (error) {
        if (!isActive) return;
        onNotify?.({
          type: "error",
          text: error.message,
        });
        onProtectedError?.(error);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    fetchOverview();

    return () => {
      isActive = false;
    };
  }, [
    departmentFilter,
    locationFilter,
    monthKey,
    onNotify,
    onProtectedError,
    searchText,
    selectedDateKey,
    token,
  ]);

  const calendar = useMemo(
    () => buildAdminAttendanceCalendar(monthKey, overview?.calendar || []),
    [monthKey, overview?.calendar]
  );

  const metrics = useMemo(
    () =>
      overview?.metrics || {
        totalEmployees: 0,
        presentToday: 0,
        absentToday: 0,
        lateToday: 0,
        onLeaveToday: 0,
      },
    [overview?.metrics]
  );

  const donutStyle = useMemo(
    () => ({ background: createAttendanceDonutStyle(metrics) }),
    [metrics]
  );

  const tableRows = overview?.tableRows || [];
  const totalPages = Math.max(1, Math.ceil(tableRows.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = tableRows.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  async function refreshOverview() {
    const payload = await getAdminAttendanceOverview(token, {
      month: monthKey,
      date: selectedDateKey,
      department: departmentFilter,
      location: locationFilter,
      search: searchText,
    });

    setOverview(payload.overview || null);
  }

  function openRecordModal(row) {
    setEditingRow(row);
    setRecordForm({
      status: row.status || "present",
      checkInTime: toTimeInputValue(row.checkInAt),
      checkOutTime: toTimeInputValue(row.checkOutAt),
      location: row.location || "Office",
    });
  }

  async function handleSubmitAttendanceRecord(event) {
    event.preventDefault();

    if (!editingRow) return;

    try {
      setIsSubmitting(true);

      await updateAdminAttendanceRecord(token, editingRow.employeeObjectId, {
        dateKey: selectedDateKey,
        ...recordForm,
      });

      setEditingRow(null);
      await refreshOverview();
      onNotify?.({
        type: "success",
        text: "Attendance updated successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRegularizationReview(attendanceId, decision) {
    try {
      setIsSubmitting(true);

      await reviewAdminAttendanceRegularization(token, attendanceId, {
        decision,
      });

      await refreshOverview();
      onNotify?.({
        type: "success",
        text:
          decision === "approved"
            ? "Regularization request approved."
            : "Regularization request rejected.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCompOffReview(attendanceId, decision) {
    try {
      setIsSubmitting(true);

      await reviewAdminCompOffRequest(token, attendanceId, {
        decision,
      });

      await refreshOverview();
      onNotify?.({
        type: "success",
        text:
          decision === "approved"
            ? "Comp off request approved."
            : "Comp off request rejected.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleImportAttendance(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsSubmitting(true);
      const text = await file.text();
      const rows = parseAttendanceCsv(text);

      if (!rows.length) {
        onNotify?.({
          type: "warning",
          text: "The CSV file is empty or does not contain valid attendance rows.",
        });
        return;
      }

      await importAdminAttendanceRecords(token, rows);
      await refreshOverview();
      onNotify?.({
        type: "success",
        text: "Attendance CSV imported successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
      event.target.value = "";
    }
  }

  function handleExportReport() {
    const rows = [
      [
        "Employee ID",
        "Employee Name",
        "Department",
        "Date",
        "Check In",
        "Check Out",
        "Status",
        "Working Hours",
        "Location",
      ],
      ...tableRows.map((row) => [
        row.employeeId,
        row.fullName,
        row.department,
        getCsvDate(row, selectedDateKey),
        row.checkInTime || "-",
        row.checkOutTime || "-",
        formatAttendanceStatus(row.status),
        row.workHoursLabel,
        row.location || "Office",
      ]),
    ];

    downloadAttendanceCsv(`instateam-admin-attendance-${selectedDateKey}.csv`, rows);
  }

  return (
    <section className="attendance-page">
      <div className="attendance-page-actions">
        <button
          type="button"
          className="secondary-button"
          onClick={() => setShowCompOffModal(true)}
        >
          Comp Off Requests
        </button>
        <button
          type="button"
          className="secondary-button"
          onClick={() =>
            pendingRequestsRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            })
          }
        >
          Regularization Requests
        </button>
        <button
          type="button"
          className="secondary-button"
          onClick={() => fileInputRef.current?.click()}
        >
          <AppIcon name="upload" />
          Import Attendance
        </button>
        <button type="button" className="primary-button" onClick={handleExportReport}>
          <AppIcon name="export" />
          Attendance Report
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={handleImportAttendance}
        />
      </div>

      <div className="metrics-grid attendance-metrics-grid">
        <MetricCard
          label="Total Employees"
          value={metrics.totalEmployees}
          helper="Active employees"
          icon="employees"
        />
        <MetricCard
          label="Present Today"
          value={metrics.presentToday}
          helper="Checked in on time"
          icon="activeUsers"
          tone="success"
        />
        <MetricCard
          label="Absent Today"
          value={metrics.absentToday}
          helper="No attendance marked"
          icon="inactiveUsers"
          tone="danger"
        />
        <MetricCard
          label="Late Today"
          value={metrics.lateToday}
          helper="Late check-ins"
          icon="attendance"
        />
        <MetricCard
          label="On Leave Today"
          value={metrics.onLeaveToday}
          helper="Approved leave"
          icon="leaves"
        />
      </div>

      <div className="attendance-main-grid">
        <div className="attendance-main-column">
          <div className="table-card attendance-calendar-card">
            <div className="attendance-calendar-topbar">
              <div>
                <h3>Attendance Calendar</h3>
              </div>

              <div className="attendance-toolbar-actions">
                <div className="attendance-inline-select">
                  <select
                    value={departmentFilter}
                    onChange={(event) => setDepartmentFilter(event.target.value)}
                  >
                    <option value="all">All Departments</option>
                    {departmentOptions.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                  <AppIcon name="chevronDown" />
                </div>

                <div className="attendance-inline-select">
                  <select
                    value={locationFilter}
                    onChange={(event) => setLocationFilter(event.target.value)}
                  >
                    <option value="all">All Locations</option>
                    {(overview?.locationOptions || []).map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                  <AppIcon name="chevronDown" />
                </div>
              </div>
            </div>

            <div className="attendance-calendar-controls">
              <button
                type="button"
                className="pagination-arrow"
                onClick={() => setMonthKey((current) => shiftMonthKey(current, -1))}
              >
                <AppIcon name="chevronLeft" />
              </button>
              <strong>{formatMonthHeading(monthKey)}</strong>
              <button
                type="button"
                className="pagination-arrow"
                onClick={() => setMonthKey((current) => shiftMonthKey(current, 1))}
              >
                <AppIcon name="chevronRight" />
              </button>
            </div>

            <div className="attendance-legend">
              {["present", "absent", "late", "weekly_off", "on_leave"].map((status) => {
                const meta = getAttendanceStatusMeta(status);

                return (
                  <span key={status} className="attendance-legend-item">
                    <em className={`attendance-dot ${meta.tone}`} />
                    {meta.label}
                  </span>
                );
              })}
            </div>

            {isLoading ? (
              <div className="directory-empty-state">Loading attendance overview...</div>
            ) : (
              <div className="attendance-calendar-grid">
                {calendar.weekdays.map((weekday) => (
                  <div key={weekday} className="attendance-calendar-weekday">
                    {weekday}
                  </div>
                ))}

                {calendar.cells.map((cell) =>
                  cell.isPlaceholder ? (
                    <div key={cell.key} className="attendance-calendar-cell placeholder" />
                  ) : (
                    <button
                      key={cell.key}
                      type="button"
                      className={`attendance-calendar-cell ${
                        cell.dateKey === selectedDateKey ? "selected" : ""
                      }`}
                      onClick={() => setSelectedDateKey(cell.dateKey)}
                    >
                      <span className="attendance-calendar-day">{cell.day}</span>
                      <div className="attendance-calendar-counts">
                        {[
                          ["present", cell.counts.present],
                          ["absent", cell.counts.absent],
                          ["late", cell.counts.late],
                        ]
                          .filter(([, value]) => value > 0)
                          .map(([status, value]) => (
                            <span
                              key={status}
                              className={`attendance-count-chip ${
                                getAttendanceStatusMeta(status).tone
                              }`}
                            >
                              {getAttendanceStatusMeta(status).shortLabel} {value}
                            </span>
                          ))}
                      </div>
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          <div className="table-card attendance-table-card">
            <div className="attendance-table-toolbar">
              <h3>Attendance Overview</h3>

              <div className="attendance-toolbar-actions">
                <label className="inline-search-field attendance-search-field">
                  <AppIcon name="search" />
                  <input
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Search employees..."
                  />
                </label>

                <div className="attendance-date-chip">
                  <AppIcon name="calendar" />
                  <input
                    type="date"
                    value={selectedDateKey}
                    onChange={(event) => setSelectedDateKey(event.target.value)}
                  />
                </div>

                <button className="secondary-button export-button" type="button" onClick={handleExportReport}>
                  <AppIcon name="export" />
                  Export
                </button>
              </div>
            </div>

            <div className="attendance-data-table">
              <div className="attendance-data-header">
                <span>Employee ID</span>
                <span>Employee Name</span>
                <span>Department</span>
                <span>Check In</span>
                <span>Check Out</span>
                <span>Status</span>
                <span>Working Hours</span>
                <span>Action</span>
              </div>

              {paginatedRows.length ? (
                paginatedRows.map((row) => {
                  const statusMeta = getAttendanceStatusMeta(row.status);

                  return (
                    <div key={`${row.employeeObjectId}-${row.employeeId}`} className="attendance-data-row">
                      <span>{row.employeeId}</span>
                      <div className="attendance-person-cell">
                        {row.profilePhoto?.url ? (
                          <img
                            src={toAbsoluteUrl(row.profilePhoto.url)}
                            alt={row.fullName}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="avatar-circle table-avatar">
                            {getInitials(row.fullName)}
                          </div>
                        )}

                        <div className="employee-cell-copy">
                          <strong>{row.fullName}</strong>
                          <span>{row.designation}</span>
                        </div>
                      </div>
                      <span>{row.department}</span>
                      <span>{row.checkInTime || "-"}</span>
                      <span>{row.checkOutTime || "-"}</span>
                      <span className={`status-badge ${statusMeta.tone}`}>
                        {formatAttendanceStatus(row.status)}
                      </span>
                      <span>{row.workHoursLabel}</span>
                      <button
                        type="button"
                        className="icon-button attendance-edit-button"
                        onClick={() => openRecordModal(row)}
                      >
                        <AppIcon name="edit" />
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="directory-empty-state">
                  No attendance rows match the current filters.
                </div>
              )}
            </div>

            <div className="table-footer">
              <span>
                Showing {tableRows.length ? (safePage - 1) * pageSize + 1 : 0} to{" "}
                {Math.min(safePage * pageSize, tableRows.length)} of {tableRows.length} employees
              </span>

              <div className="pagination">
                <button
                  className="pagination-arrow"
                  type="button"
                  onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                  disabled={safePage === 1}
                >
                  <AppIcon name="chevronLeft" />
                </button>

                {Array.from({ length: totalPages }, (_, index) => index + 1)
                  .slice(0, Math.min(totalPages, 5))
                  .map((page) => (
                    <button
                      key={page}
                      className={`page-chip ${page === safePage ? "active" : ""}`}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}

                <button
                  className="pagination-arrow"
                  type="button"
                  onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                  disabled={safePage === totalPages}
                >
                  <AppIcon name="chevronRight" />
                </button>

                <div className="page-size-shell">
                  <select
                    value={pageSize}
                    onChange={(event) => setPageSize(Number(event.target.value))}
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option} / page
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="attendance-side-column">
          <div className="table-card attendance-summary-card">
            <div className="attendance-side-heading">
              <h3>Today&apos;s Attendance Summary</h3>
            </div>

            <div className="attendance-summary-chart">
              <div className="attendance-summary-donut" style={donutStyle}>
                <div className="attendance-summary-donut-center">
                  <strong>{metrics.totalEmployees}</strong>
                  <span>Total</span>
                </div>
              </div>

              <div className="attendance-summary-list">
                {[
                  ["Present", metrics.presentToday, "success"],
                  ["Absent", metrics.absentToday, "danger"],
                  ["Late", metrics.lateToday, "warning"],
                  ["On Leave", metrics.onLeaveToday, "info"],
                ].map(([label, value, tone]) => (
                  <div key={label} className="attendance-summary-list-item">
                    <span>
                      <em className={`attendance-dot ${tone}`} />
                      {label}
                    </span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="table-card attendance-quick-actions-card">
            <div className="attendance-side-heading">
              <h3>Quick Actions</h3>
            </div>

            <div className="attendance-quick-actions-grid">
              <button type="button" className="attendance-quick-action" onClick={() => setSearchText("")}>
                <div className="attendance-quick-icon success">
                  <AppIcon name="attendance" />
                </div>
                <span>Mark Attendance</span>
              </button>

              <button
                type="button"
                className="attendance-quick-action"
                onClick={() =>
                  pendingRequestsRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                }
              >
                <div className="attendance-quick-icon warning">
                  <AppIcon name="calendar" />
                </div>
                <span>Bulk Regularization</span>
              </button>

              <button
                type="button"
                className="attendance-quick-action"
                onClick={() =>
                  onNotify?.({
                    type: "warning",
                    text: "Attendance policy can be customized in a later pass. Current rule marks check-ins after 09:15 AM as late.",
                  })
                }
              >
                <div className="attendance-quick-icon info">
                  <AppIcon name="documents" />
                </div>
                <span>Attendance Policy</span>
              </button>

              <button type="button" className="attendance-quick-action" onClick={handleExportReport}>
                <div className="attendance-quick-icon violet">
                  <AppIcon name="export" />
                </div>
                <span>Export Report</span>
              </button>
            </div>
          </div>

          <div className="table-card attendance-requests-card" ref={pendingRequestsRef}>
            <div className="attendance-side-heading">
              <h3>Pending Regularization Requests</h3>
            </div>

            <div className="attendance-requests-list">
              {(overview?.pendingRegularizations || []).length ? (
                overview.pendingRegularizations.map((record) => (
                  <div key={record._id} className="attendance-request-item">
                    <div className="attendance-request-person">
                      <div className="avatar-circle small">
                        {getInitials(record.fullName)}
                      </div>
                      <div>
                        <strong>{record.fullName}</strong>
                        <span>{record.designation}</span>
                      </div>
                    </div>

                    <div className="attendance-request-copy">
                      <span>{record.dateLabel}</span>
                      <p>{record.regularizationRequest.reason}</p>
                    </div>

                    <div className="attendance-request-actions">
                      <button
                        type="button"
                        className="status-badge active action-button"
                        onClick={() =>
                          handleRegularizationReview(record._id, "approved")
                        }
                        disabled={isSubmitting}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="status-badge inactive action-button"
                        onClick={() =>
                          handleRegularizationReview(record._id, "rejected")
                        }
                        disabled={isSubmitting}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="directory-empty-state">
                  No pending regularization requests right now.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AttendanceRecordModal
        row={editingRow}
        form={recordForm}
        onChange={setRecordForm}
        onClose={() => setEditingRow(null)}
        onSubmit={handleSubmitAttendanceRecord}
        isSubmitting={isSubmitting}
        selectedDateKey={selectedDateKey}
      />

      {showCompOffModal ? (
        <CompOffRequestsModal
          requests={overview?.pendingCompOffRequests || []}
          onClose={() => setShowCompOffModal(false)}
          onReview={handleCompOffReview}
          isSubmitting={isSubmitting}
        />
      ) : null}
    </section>
  );
}
